import { useText } from "@/lib/content";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardList, RefreshCcw, PlayCircle } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { ScoreSummary } from "@/components/assessment/ScoreSummary";
import { SubmissionTabs } from "@/components/assessment/SubmissionTabs";
import { AiChatCta } from "@/components/assessment/AiChatCta";
import { SectionTitle } from "@/components/profile/CompanyDataTab";

const Body = ({ data, restart }) => {
    const t = useText();
    const { items, draft, discarded, latest } = data;
    return (
        <>
            {discarded && (
                <Notice kind="warning" testId="assessment-discarded-notice">
                    <p className="font-semibold">{t("assessment.updated")}</p>
                    <Link to="/assessment" className="btn-primary mt-3" data-testid="assessment-discarded-start-button"><PlayCircle aria-hidden="true" size={18} /> Inizia l'assessment</Link>
                </Notice>
            )}
            {draft && (
                <Notice kind="warning" testId="assessment-draft-notice">
                    <p className="font-semibold">Hai un assessment in corso ({draft.completed_steps} di 5 step completati).</p>
                    <Link to="/assessment" className="btn-primary mt-3" data-testid="assessment-resume-button"><PlayCircle aria-hidden="true" size={18} /> Riprendi l'assessment</Link>
                </Notice>
            )}
            {!latest ? (
                <section className="edi-card p-6 sm:p-8" aria-labelledby="empty-title" data-testid="assessment-empty-state">
                    <ClipboardList aria-hidden="true" className="text-edi-text" size={36} />
                    <h3 id="empty-title" className="mt-3 text-xl sm:text-2xl font-bold">{t("profilo.empty_title")}</h3>
                    <p className="mt-2 text-edi-muted">Cinque step, circa venti domande: bastano una decina di minuti. Puoi interrompere e riprendere quando vuoi.</p>
                    {!draft && !discarded && <Link to="/assessment" className="btn-primary mt-5" data-testid="assessment-start-button">Inizia l'assessment</Link>}
                </section>
            ) : (
                <>
                    <section aria-labelledby="latest-title" className="edi-card p-5 sm:p-6" data-testid="latest-result">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 id="latest-title" className="text-xl sm:text-2xl font-bold">Ultimo risultato</h3>
                            <button type="button" className="btn-secondary" onClick={restart} data-testid="assessment-restart-button"><RefreshCcw aria-hidden="true" size={18} /> Rifai l'assessment</button>
                        </div>
                        <div className="mt-5"><ScoreSummary submission={latest} history={items} /></div>
                    </section>
                    <SubmissionTabs items={items} latest={latest} />
                </>
            )}
        </>
    );
};

export const AssessmentSection = ({ goTab, justSubmitted, onLatest, scrollHere }) => {
    const t = useText();
    const navigate = useNavigate();
    const ref = useRef(null);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [{ data: list }, { data: d }] = await Promise.all([
                    api.get("/companies/me/assessments"),
                    api.get("/companies/me/assessment-draft"),
                ]);
                const latest = list.items[0] ? (await api.get(`/companies/me/assessments/${list.items[0].id}`)).data : null;
                setData({ items: list.items, draft: d.draft, discarded: d.discarded, latest });
                onLatest?.(latest, list.items);
            } catch (e) {
                setError(errorMessage(e));
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (data && scrollHere) ref.current?.scrollIntoView({ block: "start" }); }, [data, scrollHere]);

    const restart = async () => {
        await api.delete("/companies/me/assessment-draft");
        navigate("/assessment");
    };

    return (
        <section id="sezione-assessment" ref={ref} aria-labelledby="assessment-title" className="scroll-mt-6 space-y-6" data-testid="section-assessment">
            <SectionTitle id="assessment-title">{t("profilo.assessment_title")}</SectionTitle>
            {justSubmitted && <Notice kind="success" testId="assessment-submitted-message">{t("assessment.completed")}</Notice>}
            <AiChatCta goTab={goTab} />
            {error && <Notice kind="error" role="alert">{error}</Notice>}
            {!error && (data ? <Body data={data} restart={restart} /> : <Loading />)}
        </section>
    );
};
